import { useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { usePollingManager } from '@calendly/polling'
import { cloneDeep } from 'lodash-es'
import { createContext, useEffect, useMemo, useState } from 'react'

import changeSet from 'core/change_set'
import { useFetchTemplateSummaryQuery, transformSummaries } from 'hooks/tracked_meetings/api'
import { sampleSummary } from 'hooks/tracked_meetings/utils'
import { camelize } from 'services/string_utils'

import { TARGETS, TEMPLATE_STATUS, TEMPLATE_TYPE, THUMB_FEEDBACK } from './constants'

export const TemplateSummaryContext = createContext({})
export const TemplateSummaryProvider = ({ meetingUuid, children, isSample, isExternal }) => {
  const isMiEnabled = useFlag('meeting_intelligence')
  const [template, setTemplate] = useState(TEMPLATE_TYPE.AUTO)
  const [tempTemplate, setTempTemplate] = useState('')
  const [showLeavePageModal, openLeavePageModal, closeLeavePageModal] = useBooleanState(false)
  const [isPolling, setIsPolling] = useState(false)

  const pollingConfig = usePollingManager({
    cadence: 'RAPID',
    enabled: isPolling,
    refetchOnFocus: isPolling
  })

  let { data: dataTemplateSummary, error: errorTemplateSummary, isFetching: isFetchingTemplateSummary } = useFetchTemplateSummaryQuery(
    { meetingUuid, template },
    { ...pollingConfig, skip: !template || isSample || !meetingUuid }
  )

  if (isSample) {
    dataTemplateSummary = transformSummaries(camelize(cloneDeep(sampleSummary.data)))
    errorTemplateSummary = undefined
    isFetchingTemplateSummary = false
  }

  useEffect(() => {
    if (isMiEnabled && dataTemplateSummary?.templateStatus === TEMPLATE_STATUS.REQUESTED) {
      setIsPolling(true)
    } else if (dataTemplateSummary?.templateStatus === TEMPLATE_STATUS.COMPLETED) {
      setIsPolling(false)
    }
  }, [isMiEnabled, dataTemplateSummary?.templateStatus])

  const context = useMemo(() => {
    const getContent = (target) => dataTemplateSummary?.summaries.find((summary) => summary.name === target)?.value
    const getTargetUuid = (target) => dataTemplateSummary?.summaries.find((summary) => summary.name === target)?.targetUuid
    const getThumbFeedback = (target) => {
      const targetFeedbackThumb = dataTemplateSummary?.summaries.find((summary) => summary.name === target)?.targetFeedbackThumb
      return targetFeedbackThumb ?? THUMB_FEEDBACK.NONE
    }
    const isEditedContent = (target) => {
      const content = dataTemplateSummary?.summaries.find((contentItem) => contentItem.name === target)
      return content?.edited || false
    }
    const handleTemplateSelectionClick = (selectedTemplate) => {
      if (changeSet.isDirty()) {
        setTempTemplate(selectedTemplate)
        openLeavePageModal()
      } else {
        setTemplate(selectedTemplate)
      }
    }

    return {
      template,
      dataTemplateSummary,
      summaries: {
        summary: getContent(TARGETS.SUMMARY),
        actionItems: getContent(TARGETS.ACTION_ITEMS),
        topics: getContent(TARGETS.DISCUSSION),
      },
      isSummaryEdited: isEditedContent(TARGETS.SUMMARY),
      isActionItemsEdited: isEditedContent(TARGETS.ACTION_ITEMS),
      isTopicsEdited: isEditedContent(TARGETS.DISCUSSION),
      summaryTargetUuid: getTargetUuid(TARGETS.SUMMARY),
      actionItemsTargetUuid: getTargetUuid(TARGETS.ACTION_ITEMS),
      topicsTargetUuid: getTargetUuid(TARGETS.DISCUSSION),
      summaryThumbFeedback: getThumbFeedback(TARGETS.SUMMARY),
      actionItemsThumbFeedback: getThumbFeedback(TARGETS.ACTION_ITEMS),
      topicsThumbFeedback: getThumbFeedback(TARGETS.DISCUSSION),
      errorTemplateSummary,
      isLoadingTemplateSummary: isFetchingTemplateSummary || isPolling,
      templateSummaryNotAvailable: (
        isFetchingTemplateSummary
        || isPolling
        || errorTemplateSummary !== undefined
      ),
      setTemplate,
      isSample,
      isExternal,
      handleTemplateSelectionClick,
      tempTemplate,
      setTempTemplate,
      showLeavePageModal,
      closeLeavePageModal
    }
  }, [
    template,
    tempTemplate,
    dataTemplateSummary,
    errorTemplateSummary,
    isFetchingTemplateSummary,
    isPolling,
    isSample,
    isExternal,
    showLeavePageModal,
    openLeavePageModal,
    closeLeavePageModal
  ])

  return (
    <TemplateSummaryContext.Provider value={context}>
      {children}
    </TemplateSummaryContext.Provider>
  )
}
